/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ArticleContent from 'embercom/models/articles/article-content';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';

export interface Args {
  activeContent: ArticleContent;
  showSidePanel: boolean;
  cancelEditActiveContent: () => void;
  saveActiveContent: () => void;
  publishActiveContent: () => Promise<void>;
  toggleSidePanel: () => void;
  scheduleActiveContentUpdate: (params: {
    mode: 'publish' | 'unpublish';
    scheduledDate: Date;
  }) => Promise<void>;
}

interface Signature {
  Args: Args;
}

export default class Header extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  get canUseScheduledPublishing() {
    return this.appService.app.canUseScheduledPublishing;
  }

  get scheduledContentUpdates() {
    return (this.args.activeContent.scheduledContentUpdates?.toArray() || []).filter(
      (update: ScheduledContentUpdate) => update.status === ScheduledContentUpdateStatus.PENDING,
    );
  }

  get scheduledPublishUpdates() {
    return this.scheduledContentUpdates.filter(
      (update: ScheduledContentUpdate) => update.contentUpdateType === 'publish',
    );
  }

  get scheduledUnpublishUpdates() {
    return this.scheduledContentUpdates.filter(
      (update: ScheduledContentUpdate) => update.contentUpdateType === 'unpublish',
    );
  }

  get hasScheduledContentUpdates() {
    return this.scheduledContentUpdates.length > 0;
  }

  get hasScheduledPublishUpdates() {
    return this.scheduledPublishUpdates.length > 0;
  }

  get hasScheduledUnpublishUpdates() {
    return this.scheduledUnpublishUpdates.length > 0;
  }

  get scheduleDisplayText() {
    if (!this.hasScheduledContentUpdates) {
      return null;
    }

    // Show unpublish schedule if content is published, otherwise show publish schedule
    let updateToShow = this.args.activeContent.isPublished
      ? this.scheduledUnpublishUpdates[0]
      : this.scheduledPublishUpdates[0];

    if (!updateToShow) {
      return null;
    }

    let result = ScheduledContentUpdate.getScheduleDateAndTime(
      updateToShow,
      this.appService.app.timezone,
      this.intl,
    );

    if (!result) {
      return null;
    }

    let [date, time] = result;
    let translationKey = this.args.activeContent.isPublished
      ? 'knowledge-hub.content-editor.article-content.schedule-unpublish-modal.current-schedule-short'
      : 'knowledge-hub.content-editor.article-content.schedule-modal.current-schedule-short';

    return this.intl.t(translationKey, { date, time });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::Edit::Header': typeof Header;
    'knowledge-hub/content-editor/article-content/edit/header': typeof Header;
  }
}
